import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity } from '../../utils/security';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { BarChart3, Eye, Users, Ticket, MessageSquare, Briefcase, RefreshCw, Activity } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminAnalytics = () => {
    const { t } = useTranslation();
    const { user, isAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!authLoading && !isAdmin()) navigate('/admin');
    }, [authLoading, isAdmin, navigate]);

    const fetchAnalytics = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchWithSecurity(`${API_URL}users.php?action=stats`);
            const data = await response.json();
            setStats(data);
        } catch (error) {
            toast.error(t('adminAnalytics.toastError') || 'ანალიტიკის ჩატვირთვა ვერ მოხერხდა');
        }
        setLoading(false);
    }, [t]);

    useEffect(() => {
        if (user && isAdmin()) fetchAnalytics();
    }, [user, isAdmin, fetchAnalytics]);

    if (authLoading || !user) return null;

    const totalViews = (parseInt(stats?.total_blog_views || 0) + parseInt(stats?.total_portfolio_views || 0)).toLocaleString();

    return (
        <AdminLayout title={t('adminAnalytics.title') || 'ვებგვერდის ანალიტიკა და წარმადობა'}>
            <div className="d-flex justify-content-end mb-4">
                <button className="shadcn-btn shadcn-btn-primary" onClick={fetchAnalytics}>
                    <RefreshCw size={16} /> {t('adminAnalytics.sync') || 'მონაცემების განახლება'}
                </button>
            </div>

            {loading ? (
                <div className="text-center py-5 text-muted">
                    <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                    <span>{t('adminAnalytics.loading') || 'ანალიტიკის მონაცემები იტვირთება...'}</span>
                </div>
            ) : stats ? (
                <>
                    {/* KPI Cards */}
                    <div className="row g-4 mb-4">
                        <div className="col-md-4 col-lg-2-4">
                            <div className="shadcn-stat-card">
                                <div className="stat-icon-wrapper text-indigo bg-indigo bg-opacity-20" style={{ color: '#6366f1' }}>
                                    <Eye size={22} />
                                </div>
                                <div className="stat-value">{totalViews}</div>
                                <div className="stat-label">{t('adminAnalytics.totalViews') || 'ნახვების ჯამური რაოდენობა'}</div>
                            </div>
                        </div>
                        <div className="col-md-4 col-lg-2-4">
                            <div className="shadcn-stat-card">
                                <div className="stat-icon-wrapper text-info bg-info bg-opacity-20">
                                    <Users size={22} />
                                </div>
                                <div className="stat-value">{stats?.total_users || 0}</div>
                                <div className="stat-label">{t('adminAnalytics.registeredUsers') || 'რეგისტრირებული მომხმარებლები'}</div>
                            </div>
                        </div>
                        <div className="col-md-4 col-lg-2-4">
                            <div className="shadcn-stat-card">
                                <div className="stat-icon-wrapper text-warning bg-warning bg-opacity-20">
                                    <Ticket size={22} />
                                </div>
                                <div className="stat-value">{stats?.tickets?.find(t => t.status === 'open')?.count || 0}</div>
                                <div className="stat-label">{t('adminAnalytics.openTickets') || 'ღია დახმარების ბილეთები'}</div>
                            </div>
                        </div>
                        <div className="col-md-4 col-lg-2-4">
                            <div className="shadcn-stat-card">
                                <div className="stat-icon-wrapper text-danger bg-danger bg-opacity-20">
                                    <MessageSquare size={22} />
                                </div>
                                <div className="stat-value">{stats?.total_messages || 0}</div>
                                <div className="stat-label">{t('adminAnalytics.webInquiries') || 'ვებ შეტყობინებები'}</div>
                            </div>
                        </div>
                        <div className="col-md-4 col-lg-2-4">
                            <div className="shadcn-stat-card">
                                <div className="stat-icon-wrapper text-success bg-success bg-opacity-20">
                                    <Briefcase size={22} />
                                </div>
                                <div className="stat-value">{stats?.top_portfolios?.length || 0}</div>
                                <div className="stat-label">{t('adminAnalytics.activeProjects') || 'აქტიური პროექტები'}</div>
                            </div>
                        </div>
                    </div>

                    {/* Engagement & Activity Grid */}
                    <div className="row g-4 mb-4">
                        {/* Category Engagement */}
                        <div className="col-lg-8">
                            <div className="shadcn-card h-100">
                                <div className="shadcn-card-header">
                                    <div className="shadcn-card-title">
                                        <BarChart3 size={20} className="text-indigo" />
                                        <span>{t('adminAnalytics.categoryEngagement') || 'კატეგორიების აქტივობის მეტრიკა'}</span>
                                    </div>
                                </div>

                                <div className="d-flex flex-column gap-3">
                                    {(stats?.categories || []).map((cat, idx) => {
                                        const maxViews = Math.max(...(stats?.categories || []).map(c => parseInt(c.views || 0)), 1);
                                        const percentage = Math.min((parseInt(cat.views || 0) / maxViews) * 100, 100);
                                        return (
                                            <div key={idx}>
                                                <div className="d-flex justify-content-between mb-1 small">
                                                    <span className="text-dark fw-medium">{cat.category}</span>
                                                    <span className="text-muted">{cat.views} {t('adminAnalytics.views') || 'ნახვა'}</span>
                                                </div>
                                                <div className="progress bg-slate-200" style={{ height: '8px', borderRadius: '4px' }}>
                                                    <div 
                                                        className="progress-bar bg-primary" 
                                                        style={{ width: `${percentage}%`, borderRadius: '4px' }} 
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Live Activity Feed */}
                        <div className="col-lg-4">
                            <div className="shadcn-card h-100">
                                <div className="shadcn-card-header">
                                    <div className="shadcn-card-title">
                                        <Activity size={20} className="text-indigo" />
                                        <span>{t('adminAnalytics.liveTraffic') || 'ვიზიტორთა რეალური აქტივობა'}</span>
                                    </div>
                                </div>

                                <div className="d-flex flex-column gap-3">
                                    {stats?.recent_activity?.slice(0, 5).map((act, idx) => (
                                        <div key={idx} className="p-3 rounded-3 bg-light border">
                                            <div className="text-dark small fw-medium">
                                                <span className="text-indigo fw-bold">{act.username}</span> {t('adminAnalytics.viewed') || 'ნახა'} {act.entity_title}
                                            </div>
                                            <div className="smallest text-muted mt-1">{new Date(act.viewed_at).toLocaleString()}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            ) : null}
        </AdminLayout>
    );
};

export default AdminAnalytics;
